<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">网页YI起建管理</h2>
          <p class="text-sm text-neutral-500 mt-1">
            管理前台“网页YI起建”的主推平台和平台列表
          </p>
        </div>

        <div class="flex flex-col sm:flex-row gap-3">
          <div class="relative">
            <input
              type="text"
              v-model="searchForm.keyword"
              @input="handleSearch"
              placeholder="搜索平台名称..."
              class="px-4 py-2 pl-10 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm w-full sm:w-64"
            />
            <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
          </div>
          <select
            v-model="searchForm.status"
            @change="refreshList"
            class="px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
          >
            <option value="">全部状态</option>
            <option value="1">已展示</option>
            <option value="0">已隐藏</option>
          </select>
          <button
            @click="resetSearch"
            class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 hover:border-primary hover:text-primary transition-colors text-sm"
          >
            重置
          </button>
          <button
            @click="openAddDialog"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            <i class="fa fa-plus"></i>
            新增平台
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div
          v-for="stat in stats"
          :key="stat.label"
          class="bg-neutral-50 rounded-xl border border-neutral-200 p-4"
        >
          <p class="text-sm text-neutral-500">{{ stat.label }}</p>
          <p class="text-2xl font-bold text-neutral-900 mt-2">{{ stat.value }}</p>
        </div>
      </div>

      <div v-loading="loading" class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div
          v-for="item in platformList"
          :key="item.id"
          class="bg-neutral-50 rounded-xl border border-neutral-200 p-4 sm:p-6 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex flex-col lg:flex-row gap-6">
            <div class="flex-1 min-w-0">
              <div class="flex items-start gap-4 mb-4">
                <div
                  class="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0"
                  :class="item.iconBg || 'bg-blue-100'"
                >
                  <i :class="['fa', item.icon || 'fa-window-maximize', 'text-2xl', item.iconColor || 'text-blue-600']"></i>
                </div>
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2 mb-2">
                    <h3 class="text-lg font-bold text-neutral-900">
                      {{ item.title }}
                    </h3>
                    <span
                      class="text-xs px-2 py-1 rounded-full"
                      :class="item.featured ? 'bg-orange-100 text-orange-700' : 'bg-neutral-200 text-neutral-600'"
                    >
                      {{ item.featured ? "主推平台" : "普通平台" }}
                    </span>
                    <span
                      class="text-xs px-2 py-1 rounded-full"
                      :class="item.status === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                    >
                      {{ item.status === 1 ? "已展示" : "已隐藏" }}
                    </span>
                  </div>
                  <p class="text-sm text-neutral-500 mb-2 break-all">
                    <i class="fa fa-link mr-1"></i>
                    {{ item.url }}
                  </p>
                  <p class="text-sm text-neutral-600 leading-relaxed">
                    {{ item.description }}
                  </p>
                </div>
              </div>
              <div class="flex flex-wrap gap-3 text-sm text-neutral-500">
                <span>
                  <i class="fa fa-sort-numeric-asc mr-1"></i>
                  排序 {{ item.orderNum }}
                </span>
                <span>
                  <i class="fa fa-star-o mr-1"></i>
                  {{ item.summary }}
                </span>
                <span>
                  <i class="fa fa-tag mr-1"></i>
                  {{ item.tag }}
                </span>
              </div>
            </div>

            <div class="flex lg:flex-col gap-3 border-t lg:border-t-0 lg:border-l border-neutral-200 pt-4 lg:pt-0 lg:pl-6">
              <button
                @click="toggleStatus(item)"
                class="flex-1 lg:flex-none px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center justify-center"
                :class="item.status === 1 ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100' : 'bg-green-50 text-green-700 hover:bg-green-100'"
              >
                <i class="fa mr-2" :class="item.status === 1 ? 'fa-eye-slash' : 'fa-eye'"></i>
                {{ item.status === 1 ? "隐藏" : "展示" }}
              </button>
              <button
                @click="openEditDialog(item)"
                class="flex-1 lg:flex-none px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <i class="fa fa-edit mr-2"></i>
                编辑
              </button>
              <button
                @click="deleteItem(item)"
                class="flex-1 lg:flex-none px-4 py-2 bg-neutral-50 text-neutral-600 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <i class="fa fa-trash-o mr-2"></i>
                删除
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="!loading && platformList.length === 0"
          class="bg-white rounded-xl border border-neutral-200 p-16 text-center col-span-full"
        >
          <div class="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6 text-neutral-400">
            <i class="fa fa-search text-3xl"></i>
          </div>
          <h3 class="text-lg font-medium text-neutral-900 mb-2">暂无匹配平台</h3>
          <p class="text-neutral-500 text-sm mb-6">调整搜索条件，或新增一个平台</p>
          <button
            @click="openAddDialog"
            class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            新增平台
          </button>
        </div>

        <div v-if="total > pageSize" class="flex justify-center mt-8 col-span-full">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="total"
            :page-size="pageSize"
            :current-page="page"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑平台' : '新增平台'"
      width="640px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">
            平台名称 <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.title"
            type="text"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            placeholder="请输入平台名称"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">
            访问地址 <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.url"
            type="text"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            placeholder="https://example.com"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">标签</label>
          <input
            v-model="form.tag"
            type="text"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            placeholder="精选"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">摘要</label>
          <input
            v-model="form.summary"
            type="text"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            placeholder="100+ 应用"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">图标</label>
          <select
            v-model="form.icon"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
          >
            <option value="fa-tree">树</option>
            <option value="fa-compass">指南针</option>
            <option value="fa-wrench">工具</option>
            <option value="fa-magic">创意</option>
            <option value="fa-book">学习</option>
            <option value="fa-rocket">效率</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">排序号</label>
          <input
            v-model.number="form.orderNum"
            type="number"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            placeholder="数字越小越靠前"
          />
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-neutral-700 mb-1">平台描述</label>
          <textarea
            v-model="form.description"
            rows="4"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm resize-none"
            placeholder="请输入平台介绍"
          ></textarea>
        </div>
        <label class="flex items-center gap-2 text-sm text-neutral-700">
          <input type="checkbox" v-model="form.featured" class="w-4 h-4" />
          设为主推平台
        </label>
        <label class="flex items-center gap-2 text-sm text-neutral-700">
          <input type="checkbox" v-model="form.visible" class="w-4 h-4" />
          前台展示
        </label>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <button
            @click="dialogVisible = false"
            class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-colors text-sm"
          >
            取消
          </button>
          <button
            @click="saveItem"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            确定
          </button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";

const colorMap = {
  "fa-tree": {
    iconBg: "bg-orange-100",
    iconColor: "text-orange-700",
    tagClass: "bg-orange-50 text-orange-700",
  },
  "fa-compass": {
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    tagClass: "bg-blue-50 text-blue-600",
  },
  "fa-wrench": {
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
    tagClass: "bg-green-50 text-green-600",
  },
  "fa-magic": {
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
    tagClass: "bg-purple-50 text-purple-600",
  },
  "fa-book": {
    iconBg: "bg-yellow-100",
    iconColor: "text-yellow-700",
    tagClass: "bg-yellow-50 text-yellow-700",
  },
  "fa-rocket": {
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    tagClass: "bg-red-50 text-red-600",
  },
};

export default {
  name: "WebInfoManager",
  data() {
    return {
      loading: false,
      searchForm: {
        keyword: "",
        status: "",
      },
      searchTimer: null,
      dialogVisible: false,
      isEdit: false,
      form: this.getDefaultForm(),
      platformList: [],
      page: 1,
      pageSize: 10,
      total: 0,
      allStatsList: [],
    };
  },
  computed: {
    stats() {
      const source = this.allStatsList.length > 0 ? this.allStatsList : this.platformList;
      return [
        { label: "平台总数", value: this.total || source.length },
        {
          label: "前台展示",
          value: source.filter((item) => item.status === 1).length,
        },
        {
          label: "主推平台",
          value: source.filter((item) => item.featured).length,
        },
      ];
    },
  },
  created() {
    this.getWebInfoList();
    this.getStatsList();
  },
  methods: {
    getDefaultForm() {
      return {
        id: "",
        title: "",
        url: "",
        tag: "",
        summary: "",
        description: "",
        icon: "fa-compass",
        orderNum: 0,
        featured: false,
        visible: true,
      };
    },
    async getWebInfoList() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          searchKey: this.searchForm.keyword,
        };
        if (this.searchForm.status !== "") {
          params.status = Number(this.searchForm.status);
        }

        const data = await this.$aesEncrypt(params);
        const res = await this.$axios({
          url: "webinfo/list",
          method: "POST",
          data: { data },
        });

        if (res.code === 200) {
          this.platformList = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (error) {
        console.error("获取网页YI起建列表失败:", error);
        this.platformList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    async getStatsList() {
      try {
        const data = await this.$aesEncrypt({
          page: 1,
          pageSize: 9999,
        });
        const res = await this.$axios({
          url: "webinfo/list",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.allStatsList = res.data.list || [];
        }
      } catch (error) {
        console.error("获取网页YI起建统计失败:", error);
        this.allStatsList = [];
      }
    },
    handleSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.getWebInfoList();
      }, 300);
    },
    refreshList() {
      this.page = 1;
      this.getWebInfoList();
    },
    resetSearch() {
      this.searchForm = {
        keyword: "",
        status: "",
      };
      this.page = 1;
      this.getWebInfoList();
    },
    handlePageChange(page) {
      this.page = page;
      this.getWebInfoList();
    },
    openAddDialog() {
      this.isEdit = false;
      this.form = this.getDefaultForm();
      this.dialogVisible = true;
    },
    openEditDialog(item) {
      this.isEdit = true;
      this.form = {
        id: item.id,
        title: item.title || "",
        url: item.url || "",
        tag: item.tag || "",
        summary: item.summary || "",
        description: item.description || "",
        icon: item.icon || "fa-compass",
        orderNum: item.orderNum || 0,
        featured: !!item.featured,
        visible: item.status === 1,
      };
      this.dialogVisible = true;
    },
    async saveItem() {
      if (!this.form.title.trim()) {
        ElMessage.warning("请输入平台名称");
        return;
      }
      if (!this.form.url.trim()) {
        ElMessage.warning("请输入访问地址");
        return;
      }

      const style = colorMap[this.form.icon] || colorMap["fa-compass"];
      const payload = {
        title: this.form.title.trim(),
        url: this.form.url.trim(),
        tag: this.form.tag.trim() || "平台",
        summary: this.form.summary.trim() || "应用集合",
        description: this.form.description.trim(),
        icon: this.form.icon,
        orderNum: this.form.orderNum || 0,
        featured: this.form.featured,
        status: this.form.visible ? 1 : 0,
        ...style,
      };

      if (this.isEdit) {
        payload.id = this.form.id;
      }

      try {
        const data = await this.$aesEncrypt(payload);
        const res = await this.$axios({
          url: "webinfo",
          method: "PUT",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success(this.isEdit ? "编辑成功" : "新增成功");
          this.dialogVisible = false;
          this.getWebInfoList();
          this.getStatsList();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (error) {
        console.error("保存网页YI起建失败:", error);
        ElMessage.error("保存失败，请检查网络");
      }
    },
    async toggleStatus(item) {
      const nextStatus = item.status === 1 ? 0 : 1;
      try {
        const res = await this.$axios({
          url: `webinfo/status/${item.id}/${nextStatus}`,
          method: "PUT",
        });
        if (res.code === 200) {
          ElMessage.success(nextStatus === 1 ? "已设为展示" : "已设为隐藏");
          this.getWebInfoList();
          this.getStatsList();
        } else {
          ElMessage.error(res.msg || "状态更新失败");
        }
      } catch (error) {
        console.error("更新网页YI起建状态失败:", error);
        ElMessage.error("状态更新失败，请检查网络");
      }
    },
    deleteItem(item) {
      ElMessageBox.confirm(
        `确定删除平台"${item.title}"吗？`,
        "删除平台",
        {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "error",
        }
      ).then(async () => {
        try {
          const res = await this.$axios({
            url: `webinfo/${item.id}`,
            method: "DELETE",
          });
          if (res.code === 200) {
            ElMessage.success("删除成功");
            this.getWebInfoList();
            this.getStatsList();
          } else {
            ElMessage.error(res.msg || "删除失败");
          }
        } catch (error) {
          console.error("删除网页YI起建失败:", error);
          ElMessage.error("删除失败，请检查网络");
        }
      });
    },
  },
};
</script>
